import { useTitanApp } from "@/hooks/useTitanApp";
import { store } from "@/store/store";
import type {
  PreConsultation,
  AppointmentAction,
  AppointmentVaccine,
} from "./appointmentsData";
import { initialMembers, type Member } from "./membersData";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo, useCallback } from "react";
import {
  addBlockOut,
  deleteBlockOut,
  addScheduleEvent,
  deleteScheduleEvents,
  refreshScheduleEventsSilently,
} from "@/dispatchers/scheduleEventsDispatchers.js";
import {
  getPatientConsultations,
  getPatientByPatientId,
  checkAndAddConsultation,
  getPatientConsultationByConsultationId,
  setPreConsultationTemplate,
  setPreConsultationTranscription,
  getPreConsultationByConsultationId,
  getPaymentAuditByConsultation,
  setPatientTabSwitcher,
  updateConsultationActions,
  SaveAIChatMessage,
  updateEventStatus,
  updateConsultationStatus,
} from "@/dispatchers/patientDispatchers";
import {
  setPatientInfoTab,
  setisPatientSearched,
  setCurrentGlobalTabValue,
  setCurrentGlobalTabText,
  setRoutedFromScheduler,
} from "@/dispatchers/appDispatchers";
import { setShowNHS111ReferralTab } from "../../dispatchers/pharmacyServiceDispatchers";
import { setIsTemplateSelected } from "@/dispatchers/templateDispatchers";
import { v4 as uuidv4 } from "uuid";

export interface Booking {
  id: string;
  consultationID?: string; // Consultation ID (same as id for consistency with old calendar)
  patientName: string;
  patientId?: string; // Patient ID from API
  pharmacist: string;
  serviceType: "Earwax" | "Travel" | "Weight" | "Covid";
  serviceName?: string; // Service name from API (for consistency with old calendar)
  serviceId?: string; // Service ID from API (used to look up real service data)
  serviceColor?: string; // Service color from API (for UI display)
  date: string;
  time: string;
  duration: number; // in minutes
  room?: string; // Room name/ID for the consultation
  notes?: string;
  status?:
    | "Pending"
    | "Started"
    | "Completed"
    | "Cancelled"
    | "Removed"
    | "Missed"
    | "confirmed";
  eventStatus?: number; // Event status code from API
  contactNumber?: string;
  email?: string;
  otherPharmacyId?: string; // Other pharmacy ID if applicable
  otherPharmacyName?: string; // Other pharmacy name if applicable
  preConsultation?: PreConsultation;
  tasksToComplete?: AppointmentAction[]; // Tasks/actions to be completed during this appointment
  vaccinesToAdminister?: AppointmentVaccine[]; // Vaccines to be administered during this appointment
}

export interface BlockOut {
  id: string;
  title: string;
  type: "team_event" | "training" | "maintenance" | "break" | "other";
  date: string;
  startDate?: string;
  endDate?: string;
  startTime: string;
  endTime: string;
  description?: string;
  affectedRooms?: string[]; // Which rooms are blocked
  affectedPharmacists?: string[]; // Which pharmacists are unavailable
  isRecurring?: boolean;
  recurringPattern?: "daily" | "weekly" | "monthly";
  color?: string;
}

export interface Task {
  id: string;
  title: string;
  description?: string;
  assignedTo: string; // Member name
  assignedBy: string; // Member name who created the task
  dueDate: string; // Date in YYYY-MM-DD format
  dueTime?: string; // Optional time in HH:MM format
  priority: "low" | "medium" | "high" | "urgent";
  status: "todo" | "in_progress" | "completed" | "cancelled";
  category:
    | "administrative"
    | "clinical"
    | "inventory"
    | "maintenance"
    | "training"
    | "other";
  createdAt: string;
  completedAt?: string;
  notes?: string;
  AdditionalNotes?: string;
  additionalNotes?: string;
  attachments?: string[]; // URLs to attachments
}
// Room interface matching settings structure
export interface Room {
  id: string;
  name: string;
  services: string[];
  availability: {
    monday: { enabled: boolean; timeBlocks: { from: string; to: string }[] };
    tuesday: { enabled: boolean; timeBlocks: { from: string; to: string }[] };
    wednesday: { enabled: boolean; timeBlocks: { from: string; to: string }[] };
    thursday: { enabled: boolean; timeBlocks: { from: string; to: string }[] };
    friday: { enabled: boolean; timeBlocks: { from: string; to: string }[] };
    saturday: { enabled: boolean; timeBlocks: { from: string; to: string }[] };
    sunday: { enabled: boolean; timeBlocks: { from: string; to: string }[] };
  };
  active: boolean;
}
// Block-out types and configuration - constant UI values
export const blockOutTypes = [
  { value: "team_event", label: "Team Event", color: "bg-indigo-500" },
  { value: "training", label: "Training Session", color: "bg-green-500" },
  { value: "maintenance", label: "Maintenance", color: "bg-yellow-500" },
  { value: "break", label: "Break/Lunch", color: "bg-gray-500" },
  { value: "other", label: "Other", color: "bg-purple-500" },
] as const;

// Task categories and priorities
export const taskCategories = [
  { value: "administrative", label: "Administrative", icon: "📋" },
  { value: "clinical", label: "Clinical", icon: "⚕️" },
  { value: "inventory", label: "Inventory", icon: "📦" },
  { value: "maintenance", label: "Maintenance", icon: "🔧" },
  { value: "training", label: "Training", icon: "📚" },
  { value: "other", label: "Other", icon: "📌" },
] as const;

export const taskPriorities = [
  {
    value: "low",
    label: "Low",
    color: "bg-gray-500",
    textColor: "text-gray-700",
    bgLight: "bg-gray-100",
  },
  {
    value: "medium",
    label: "Medium",
    color: "bg-blue-500",
    textColor: "text-blue-700",
    bgLight: "bg-blue-100",
  },
  {
    value: "high",
    label: "High",
    color: "bg-orange-500",
    textColor: "text-orange-700",
    bgLight: "bg-orange-100",
  },
  {
    value: "urgent",
    label: "Urgent",
    color: "bg-red-500",
    textColor: "text-red-700",
    bgLight: "bg-red-100",
  },
] as const;

// export const serviceTypes = ["Earwax", "Travel", "Weight", "Covid"];

// Helper: Convert date string to {date, time} format
const getDateTimeParts = (dateStr: string) => {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) {
    return { date: "", time: "" };
  }
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  const hours = String(d.getUTCHours()).padStart(2, "0");
  const minutes = String(d.getUTCMinutes()).padStart(2, "0");
  return {
    date: `${year}-${month}-${day}`,
    time: `${hours}:${minutes}`,
  };
};

// Helper: Generate initials from name
const generateInitials = (name: string): string => {
  if (!name || name.trim() === "") return "NA";
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

// Transform API schedule events to bookings
// ScheduleEventType enum: Appointment=1, TeamEvent=2, TrainingSession=3, Maintenance=4, Break=5, Other=6, Task=7
const SCHEDULE_EVENT_TYPE = {
  Appointment: 1,
  TeamEvent: 2,
  TrainingSession: 3,
  Maintenance: 4,
  Break: 5,
  Other: 6,
  Task: 7,
};

const transformAPIEventsToBookings = (
  scheduleEvents: any[],
  resources: any[],
  services: any[],
): Booking[] => {
  const bookings: Booking[] = [];
  (scheduleEvents || []).forEach((event) => {
    const eventType = Number(
      event.ScheduleEventType !== undefined
        ? event.ScheduleEventType
        : event.scheduleEventType,
    );
    // Filter: Only include Appointments (ScheduleEventType = 1)
    if (eventType !== SCHEDULE_EVENT_TYPE.Appointment) {
      return;
    }

    const resource = resources.find((r: any) => r.id === event.ResourceId);

    const service = services.find((s: any) => s._id === event.ServiceID);

    // Get service type from service name or service description
    const serviceName = service?.serviceName || "";
    const serviceType = service?.serviceType || "";
    const { date, time } = getDateTimeParts(event.StartDate);

    let duration = 30;
    if (event.StartDate && event.EndDate) {
      try {
        const start = new Date(event.StartDate);
        const end = new Date(event.EndDate);
        const diffInMinutes = (end.getTime() - start.getTime()) / (1000 * 60);
        if (diffInMinutes > 0) {
          duration = Math.round(diffInMinutes * 100) / 100;
        }
      } catch {
        if (event.Duration) {
          duration = event.Duration;
        }
      }
    } else if (event.Duration) {
      duration = event.Duration;
    }

    const isPharmacist = resource?.resourceType === 1;
    const roomResource = event.RoomId
      ? resources.find((r: any) => String(r.id) === String(event.RoomId))
      : undefined;

    // Keep event identity for updates/deletes, while preserving consultation identity separately.
    const eventId = event.EventId || event._id;
    const consultationId = event.ConsultationID || eventId;
    const rawTasks = event.TasksToComplete || event.tasksToComplete || [];
    const tasksToComplete = Array.isArray(rawTasks)
      ? rawTasks.map((t: any, idx: number) => {
          if (typeof t === "string") {
            return { id: String(idx + 1), label: t, text: t, completed: false };
          }
          return {
            id: t.id || t.Id || t._id || String(idx + 1),
            label: t.label || t.text || t.name || t.Name || String(t),
            text: t.text || t.label || t.name || t.Name || String(t),
            completed: Boolean(t.completed),
          };
        })
      : [];

    const rawVaccines =
      event.VaccinesAdminister ||
      event.vaccinesAdminister ||
      event.VaccinesToAdminister ||
      [];
    const vaccinesAdminister = Array.isArray(rawVaccines)
      ? rawVaccines.map((v: any, idx: number) => {
          if (typeof v === "string") {
            return { id: String(idx + 1), name: v, vaccineName: v, dose: "" };
          }
          const vName =
            v.VaccineName || v.vaccineName || v.Name || v.name || "Vaccine";
          return {
            id: v.Id || v.id || v._id || String(idx + 1),
            name: vName,
            vaccineName: vName,
            dose: v.Manufacturer || v.dose || "",
            manufacturer: v.Manufacturer || "",
            prescriptionItemId: v.PrescriptionItemId || "",
          };
        })
      : [];

    // Map all possible field names for additional properties
    const booking: Booking = {
      id: eventId,
      consultationID: consultationId,
      patientName: event.PatientName || event.Name || "Patient",
      patientId: event.PatientID || event.patientId,
      pharmacist: resource
        ? resource.name
        : event.CalendarName ||
          event.CalenderName ||
          event.ResourceId ||
          "Unassigned",
      serviceType: serviceType as any,
      serviceName: serviceName, // Use the resolved service name
      serviceId: event.NamesOfConsultantServiceID || event.ServiceID, // Use correct API field name
      serviceColor: service?.serviceColor || "#9ca3af",
      date,
      time,
      duration,
      room: roomResource
        ? roomResource.name
        : event.RoomName || event.Room || event.RoomId,
      roomId: event.RoomId,
      notes: event.Name || "",
      status: event.Status || event.status || "Active",
      eventStatus: event.EventStatus,
      email: event.Email || event.email || event.PatientEmail,
      contactNumber:
        event.Mobile || event.mobile || event.contactNumber || event.Phone,
      otherPharmacyId: event.otherPharmacyId || event.OtherPharmacyId,
      otherPharmacyName: event.otherPharmacyName || event.OtherPharmacyName,
      tasksToComplete,
      vaccinesAdminister,
      vaccinesToAdminister: vaccinesAdminister,
    };

    bookings.push(booking);
  });

  return bookings.sort((a, b) => {
    const dateCompare = a.date.localeCompare(b.date);
    if (dateCompare !== 0) return dateCompare;
    return a.time.localeCompare(b.time);
  });
};

// Transform API events to block-outs (filter out appointments and tasks, only include TeamEvent, TrainingSession, Maintenance, Break, Other)
const transformAPIEventsToBlockOuts = (
  scheduleEvents: any[],
  resources: any[],
): BlockOut[] => {
  const blockOutsMap = new Map<string, BlockOut>();

  const eventTypeToBlockOutType = (eventType: number) => {
    switch (eventType) {
      case SCHEDULE_EVENT_TYPE.TeamEvent:
        return "team_event";
      case SCHEDULE_EVENT_TYPE.TrainingSession:
        return "training";
      case SCHEDULE_EVENT_TYPE.Maintenance:
        return "maintenance";
      case SCHEDULE_EVENT_TYPE.Break:
        return "break";
      default:
        return "other";
    }
  };

  const eventTypeToColor = (eventType: number) => {
    switch (eventType) {
      case SCHEDULE_EVENT_TYPE.TeamEvent:
        return "bg-indigo-500";
      case SCHEDULE_EVENT_TYPE.TrainingSession:
        return "bg-green-500";
      case SCHEDULE_EVENT_TYPE.Maintenance:
        return "bg-yellow-500";
      case SCHEDULE_EVENT_TYPE.Break:
        return "bg-gray-500";
      default:
        return "bg-purple-500";
    }
  };

  (scheduleEvents || []).forEach((event) => {
    const eventType = Number(
      event.ScheduleEventType !== undefined
        ? event.ScheduleEventType
        : event.scheduleEventType,
    );
    // Filter: Only include non-appointment, non-task events (types 2-6)
    if (
      eventType === SCHEDULE_EVENT_TYPE.Appointment ||
      eventType === SCHEDULE_EVENT_TYPE.Task
    ) {
      return;
    }

    const blockOutType = eventTypeToBlockOutType(eventType);
    const { date: startDate, time: startTime } = getDateTimeParts(
      event.StartDate,
    );
    const { date: endDate, time: endTime } = getDateTimeParts(
      event.EndDate || event.StartDate,
    );

    const resourceType = Number(
      event.ResourceType !== undefined ? event.ResourceType : event.resourceType,
    );

    const isRoomResource =
      resourceType === 2 ||
      event.ResourceType === "Room" ||
      (resources || []).some(
        (r: any) =>
          (r.type === "Consultation Room" || r.isRoom) &&
          (String(r.id) === String(event.ResourceId) ||
            String(r._id) === String(event.ResourceId)),
      );

    const resource = (resources || []).find(
      (r: any) => String(r.id || r._id) === String(event.ResourceId),
    );
    const resourceName = resource
      ? resource.name
      : event.CalenderName || event.ResourceId;

    const rawAffectedRooms: string[] = Array.isArray(
      event.affectedRooms || event.AffectedRooms,
    )
      ? event.affectedRooms || event.AffectedRooms
      : [];
    const rawAffectedPharmacists: string[] = Array.isArray(
      event.affectedPharmacists || event.AffectedPharmacists,
    )
      ? event.affectedPharmacists || event.AffectedPharmacists
      : [];

    const itemAffectedRooms: string[] = [];
    const itemAffectedPharmacists: string[] = [];

    rawAffectedRooms.forEach((r: string) => {
      const rStr = String(r).trim();
      if (!rStr || rStr.includes("@")) return;
      if (!itemAffectedRooms.includes(rStr)) {
        itemAffectedRooms.push(rStr);
      }
    });

    rawAffectedPharmacists.forEach((p: string) => {
      const pStr = String(p).trim();
      if (!pStr) return;
      if (!itemAffectedPharmacists.includes(pStr)) {
        itemAffectedPharmacists.push(pStr);
      }
    });

    if (isRoomResource) {
      const roomVal = resource ? resource.name : (event.CalenderName || event.ResourceId);
      if (roomVal && !itemAffectedRooms.includes(roomVal)) {
        itemAffectedRooms.push(roomVal);
      }
      if (event.ResourceId && !itemAffectedRooms.includes(event.ResourceId)) {
        itemAffectedRooms.push(event.ResourceId);
      }
    } else {
      const pharmVal = resource ? resource.name : (event.CalenderName || event.ResourceId);
      if (pharmVal && !itemAffectedPharmacists.includes(pharmVal)) {
        itemAffectedPharmacists.push(pharmVal);
      }
      if (event.ResourceId && !itemAffectedPharmacists.includes(event.ResourceId)) {
        itemAffectedPharmacists.push(event.ResourceId);
      }
    }

    const id = event.EventId || event._id;

    if (blockOutsMap.has(id)) {
      const existing = blockOutsMap.get(id)!;
      itemAffectedRooms.forEach((r) => {
        if (!existing.affectedRooms?.includes(r)) {
          existing.affectedRooms?.push(r);
        }
      });
      itemAffectedPharmacists.forEach((p) => {
        if (!existing.affectedPharmacists?.includes(p)) {
          existing.affectedPharmacists?.push(p);
        }
      });
    } else {
      const blockOut: BlockOut = {
        id,
        title: event.Name || event.CalenderName || "Block Out",
        type: blockOutType as any,
        date: startDate,
        startDate,
        endDate: endDate || startDate,
        startTime,
        endTime,
        description: event.Description || undefined,
        affectedRooms: itemAffectedRooms,
        affectedPharmacists: itemAffectedPharmacists,
        color: eventTypeToColor(eventType),
      };
      blockOutsMap.set(id, blockOut);
    }
  });

  return Array.from(blockOutsMap.values());
};

// Transform API events to tasks (ScheduleEventType = 7)
const transformAPIEventsToTasks = (
  scheduleEvents: any[],
  resources: any[],
): Task[] => {
  const tasks: Task[] = [];
  (scheduleEvents || []).forEach((event) => {
    const eventType = Number(
      event.ScheduleEventType !== undefined
        ? event.ScheduleEventType
        : event.scheduleEventType,
    );

    // Filter: Only include Tasks (ScheduleEventType = 7)
    if (eventType !== SCHEDULE_EVENT_TYPE.Task) {
      return;
    }

    const { date: dueDate, time: dueTime } = getDateTimeParts(event.StartDate);
    const resource = resources.find(
      (r: any) => r.id === event.ResourceId || r.name === event.ResourceId,
    );
    const assignedTo = resource
      ? resource.name
      : event.CalenderName || event.ResourceId || "Unassigned";

    const priorityVal = (
      event.Priority ||
      event.priority ||
      event.Cls ||
      "medium"
    ).toLowerCase();
    const priority = ["low", "medium", "high", "urgent"].includes(priorityVal)
      ? priorityVal
      : "medium";

    const categoryVal = (
      event.Category ||
      event.category ||
      "administrative"
    ).toLowerCase();
    const category = [
      "administrative",
      "clinical",
      "inventory",
      "maintenance",
      "training",
      "other",
    ].includes(categoryVal)
      ? categoryVal
      : "administrative";

    const statusVal = (
      event.Status ||
      event.status ||
      (event.EventStatus === 3
        ? "completed"
        : event.EventStatus === 2
          ? "in_progress"
          : "todo")
    ).toLowerCase();
    const status = ["todo", "in_progress", "completed", "cancelled"].includes(
      statusVal,
    )
      ? statusVal
      : "todo";

    const taskId = event.EventId || event._id || `task_${Date.now()}`;
    const notesText =
      event.AdditionalNotes ||
      event.additionalNotes ||
      event.Notes ||
      event.notes ||
      "";

    const task: Task = {
      id: taskId,
      title: event.Name || event.CalenderName || event.title || "Task",
      description: event.Description || event.description || "",
      assignedTo: event.ResourceId || assignedTo,
      assignedBy: event.AssignedBy || "Current User",
      dueDate: dueDate,
      dueTime: dueTime,
      priority: priority as any,
      status: status as any,
      category: category as any,
      createdAt: event.CreatedAt || new Date().toISOString(),
      notes: notesText,
      AdditionalNotes: notesText,
      additionalNotes: notesText,
    };

    tasks.push(task);
  });

  return tasks;
};

// Extract pharmacists from resources
const extractPharmacistsFromResources = (resources: any[]): string[] => {
  return (resources || [])
    .filter((r: any) => r.resourceType === 1)
    .map((r: any) => r.name)
    .filter(Boolean);
};

const extractPharmacists = (
  resources: any[],
): { id: string; name: string }[] => {
  return (resources || [])
    .filter((r: any) => r.resourceType === 1)
    .map((r: any) => ({
      id: r.id,
      name: r.name,
    }))
    .filter((r) => r.name);
};

// Extract rooms from resources
const extractRoomsFromResources = (resources: any[]): string[] => {
  return (resources || [])
    .filter((r: any) => r.resourceType === 2)
    .map((r: any) => r.name)
    .filter(Boolean);
};
// Extract service types from services
const extractServiceTypesFromServices = (services: any[]): string[] => {
  return (services || []).map((s: any) => s.serviceName).filter(Boolean);
};

// Extract complete service info including colors (similar to Scheduler)
const extractServiceTypesInfo = (services: any[]): ServiceTypeInfo[] => {
  return (services || []).map((service: any) => ({
    _id: service._id || "",
    name: service.serviceName || "",
    label: service.serviceName || "",
    serviceColor: service.serviceColor || "#9ca3af",
    serviceType: service.serviceType || 0,
    serviceCharges: service.serviceCharges || 0,
    serviceDuration: service.serviceDuration || 0,
    isDefault: service.isDefault || false,
    companyID: service.companyID || "",
  }));
};

export interface ServiceTypeInfo {
  _id: string;
  name: string;
  label: string;
  serviceColor: string;
  serviceType: number;
  serviceCharges: number;
  serviceDuration: number;
  isDefault: boolean;
  companyID: string;
}

// Extract service colors and create mappings from API data
const createServiceColorMap = (services: any[]): Record<string, string> => {
  const colorMap: Record<string, string> = {};
  const predefinedColors: Record<string, string> = {
    Earwax: "#fbbf24",
    Travel: "#fb923c",
    Weight: "#10b981",
  };

  // Palette of colors for auto-assignment
  const colorPalette = [
    "#3b82f6", // blue
    "#ef4444", // red
    "#8b5cf6", // violet
    "#ec4899", // pink
    "#f59e0b", // amber
    "#06b6d4", // cyan
    "#10b981", // emerald
    "#f97316", // orange
    "#6366f1", // indigo
    "#d946ef", // fuchsia
  ];

  (services || []).forEach((service: any, index: number) => {
    if (service.serviceName) {
      // Use API color if available, otherwise try predefined colors, otherwise pick from palette
      if (service.serviceColor && service.serviceColor.trim()) {
        colorMap[service.serviceName] = service.serviceColor;
      } else if (predefinedColors[service.serviceName]) {
        colorMap[service.serviceName] = predefinedColors[service.serviceName];
      } else {
        // Assign from color palette cyclically
        colorMap[service.serviceName] =
          colorPalette[index % colorPalette.length];
      }
    }
  });
  return colorMap;
};

// Create service name mapping from service ID
const createServiceNameMap = (services: any[]): Record<string, string> => {
  const nameMap: Record<string, string> = {};
  (services || []).forEach((service: any) => {
    if (service._id && service.serviceName) {
      nameMap[service._id] = service.serviceName;
    }
  });
  return nameMap;
};

/**
 * Check if a resource (pharmacist or room) is available for a given day of week, optional time string, and optional selected service types.
 *
 * Rules:
 * - dayOfWeek: 0 = Sunday, 1 = Monday, 2 = Tuesday, 3 = Wednesday, 4 = Thursday, 5 = Friday, 6 = Saturday.
 * - If slots array for a specific dayOfWeek is empty ([]), the entire day is blocked for that resource/service.
 * - If slots contains time ranges, only those time ranges remain available, and all other time slots are blocked.
 * - If selectedServiceTypes is provided, only services matching the selected service types are checked.
 * - If timeString is omitted, checks if the day has at least one available slot.
 */
export const isResourceAvailable = (
  resource: any,
  dayOfWeek: number,
  timeString?: string,
  selectedServiceTypes?: string[],
  allUserWeekAvailability?: any[],
): boolean => {
  if (!resource) return true;

  // Retrieve user availability list from parameter or Redux store fallback
  const userAvailabilityList =
    allUserWeekAvailability && allUserWeekAvailability.length > 0
      ? allUserWeekAvailability
      : (store.getState()?.scheduleEvent as any)?.allUserWeekAvailability || [];

  const resourceEmail = String(
    resource.id || resource.email || resource.userEmail || "",
  )
    .toLowerCase()
    .trim();

  // If user availability data exists and matches the resource id/userEmail
  if (
    userAvailabilityList &&
    Array.isArray(userAvailabilityList) &&
    userAvailabilityList.length > 0 &&
    resourceEmail
  ) {
    const userRecord = userAvailabilityList.find((u: any) => {
      const uEmail = String(u.userEmail || u.email || "")
        .toLowerCase()
        .trim();
      return uEmail && uEmail === resourceEmail;
    });

    if (userRecord && Array.isArray(userRecord.userWeekAvailability)) {
      const dayAvail = userRecord.userWeekAvailability.find(
        (a: any) => Number(a.dayOfWeek) === Number(dayOfWeek),
      );

      // If day entry does not exist, or slots array is missing / empty ([]), block the entire day!
      if (
        !dayAvail ||
        !dayAvail.slots ||
        !Array.isArray(dayAvail.slots) ||
        dayAvail.slots.length === 0
      ) {
        return false;
      }

      // If whole-day check (no specific time requested) -> since slots.length > 0, available today
      if (!timeString) {
        return true;
      }

      const parseTimeToMinutes = (tStr: string): number | null => {
        if (!tStr || typeof tStr !== "string") return null;
        const parts = tStr.split(":").map(Number);
        if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) return null;
        return parts[0] * 60 + parts[1];
      };

      const targetMinutes = parseTimeToMinutes(timeString);
      if (targetMinutes === null) return true;

      // Check if targetMinutes falls inside [startTime, endTime) of any slot
      return dayAvail.slots.some((slot: any) => {
        const startMin = parseTimeToMinutes(slot.startTime);
        let endMin = parseTimeToMinutes(slot.endTime);
        if (startMin === null || endMin === null) return false;

        // Handle midnight / end of day ("00:00", "24:00", or endMin <= startMin)
        if (
          slot.endTime === "00:00" ||
          slot.endTime === "24:00" ||
          endMin <= startMin
        ) {
          endMin = 1440;
        }

        return targetMinutes >= startMin && targetMinutes < endMin;
      });
    }
  }

  // Fallback to resource service availability if no user availability record match found
  if (
    !resource.services ||
    !Array.isArray(resource.services) ||
    resource.services.length === 0
  ) {
    return true; // No services configuration -> default available
  }

  const normalize = (str: any) =>
    String(str || "")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "");

  let targetServices = resource.services.filter(
    (s: any) => s && s.isActive !== false,
  );

  if (targetServices.length === 0) {
    return true;
  }

  // Filter by selected service types if provided
  if (selectedServiceTypes && selectedServiceTypes.length > 0) {
    const normFilters = selectedServiceTypes.map(normalize);
    targetServices = targetServices.filter((s: any) => {
      const sName = normalize(s.serviceName);
      const sId = normalize(s._id || s.id);
      const sType = normalize(s.serviceType);
      const sCode = normalize(s.serviceCode);

      return normFilters.some(
        (f) =>
          f &&
          (sName === f ||
            sName.includes(f) ||
            f.includes(sName) ||
            sId === f ||
            sType === f ||
            sCode === f),
      );
    });

    if (targetServices.length === 0) {
      return false;
    }
  }

  const parseTimeToMinutes = (tStr: string): number | null => {
    if (!tStr || typeof tStr !== "string") return null;
    const parts = tStr.split(":").map(Number);
    if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) return null;
    return parts[0] * 60 + parts[1];
  };

  const targetMinutes = timeString ? parseTimeToMinutes(timeString) : null;

  return targetServices.some((service: any) => {
    if (
      !service.availability ||
      !Array.isArray(service.availability) ||
      service.availability.length === 0
    ) {
      return true;
    }

    const dayAvail = service.availability.find(
      (a: any) => Number(a.dayOfWeek) === Number(dayOfWeek),
    );

    if (
      !dayAvail ||
      !dayAvail.slots ||
      !Array.isArray(dayAvail.slots) ||
      dayAvail.slots.length === 0
    ) {
      return false;
    }

    if (targetMinutes === null) {
      return true;
    }

    return dayAvail.slots.some((slot: any) => {
      const startMin = parseTimeToMinutes(slot.startTime);
      let endMin = parseTimeToMinutes(slot.endTime);
      if (startMin === null || endMin === null) return false;
      if (
        slot.endTime === "00:00" ||
        slot.endTime === "24:00" ||
        endMin <= startMin
      ) {
        endMin = 1440;
      }
      return targetMinutes >= startMin && targetMinutes < endMin;
    });
  });
};

const isResourceAvailableForService = (
  resource: any,
  serviceName: string,
  dayOfWeek: number,
  timeString: string,
): boolean => {
  return isResourceAvailable(
    resource,
    dayOfWeek,
    timeString,
    serviceName ? [serviceName] : undefined,
  );
};

/**
 * Get member availability from API resources
 * If member exists in resources with service availability, use that
 * Otherwise fall back to initialMembers
 */
const getMemberAvailabilityFromResources = (
  memberName: string,
  resources: any[],
): any => {
  if (!resources || resources.length === 0) {
    return null;
  }

  const resource = resources.find(
    (r: any) => r.name === memberName && r.resourceType === 1,
  );
  return resource || null;
};

export const serviceTypesInfo = [
  {
    name: "Earwax",
    label: "Ear Wax Removal",
    duration: "30 min",
    color: "bg-yellow-400",
  },
  {
    name: "Covid",
    label: "Flu Vaccination",
    duration: "15 min",
    color: "bg-purple-400",
  },
  {
    name: "Weight",
    label: "Men's Health",
    duration: "45 min",
    color: "bg-green-400",
  },
  {
    name: "Travel",
    label: "Travel Health",
    duration: "60 min",
    color: "bg-orange-400",
  },
];

// Generate dates for the current month and next month
const generateDates = () => {
  const dates = [];
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();

  // Generate dates for current month
  for (let day = 1; day <= 31; day++) {
    const date = new Date(currentYear, currentMonth, day);
    if (date.getMonth() === currentMonth) {
      dates.push(date.toISOString().split("T")[0]);
    }
  }

  // Generate dates for next month
  for (let day = 1; day <= 31; day++) {
    const date = new Date(currentYear, currentMonth + 1, day);
    if (date.getMonth() === (currentMonth + 1) % 12) {
      dates.push(date.toISOString().split("T")[0]);
    }
  }

  return dates;
};

// Utility function to check if a member is working at a specific day and time
export const isMemberWorkingAt = (
  member: Member | undefined,
  dayOfWeek: number, // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  timeString: string, // Format: "HH:MM"
): boolean => {
  if (!member || !member.availability) return true; // If no availability data, assume always working

  const dayNames = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ] as const;
  const dayName = dayNames[dayOfWeek];
  const dayAvailability = member.availability[dayName];

  if (!dayAvailability.enabled) return false;

  // Convert time string to minutes for comparison
  const [hours, minutes] = timeString.split(":").map(Number);
  const timeInMinutes = hours * 60 + minutes;

  // Check if time falls within any time block
  return dayAvailability.timeBlocks.some((block) => {
    const [fromHours, fromMinutes] = block.from.split(":").map(Number);
    const [toHours, toMinutes] = block.to.split(":").map(Number);
    const fromInMinutes = fromHours * 60 + fromMinutes;
    const toInMinutes = toHours * 60 + toMinutes;

    return timeInMinutes >= fromInMinutes && timeInMinutes < toInMinutes;
  });
};

// Get member by full name (format: "FirstName LastName")
export const getMemberByFullName = (fullName: string): Member | undefined => {
  return initialMembers.find((member) => {
    const memberFullName = `${member.forename} ${member.surname}`;
    return memberFullName === fullName;
  });
};

/**
 * Check if a pharmacist/resource is available for a service at a given time
 * using API resource availability data
 */
export const isResourceAvailableForServiceAtTime = (
  resource: any,
  serviceName: string,
  dayOfWeek: number,
  timeString: string,
): boolean => {
  return isResourceAvailable(
    resource,
    dayOfWeek,
    timeString,
    serviceName ? [serviceName] : undefined,
  );
};

// Utility functions
export const getBookingsForDate = (
  bookings: Booking[],
  date: string,
): Booking[] => {
  return bookings.filter((booking) => booking.date === date);
};

const parseTimeToMinutes = (time?: string): number | null => {
  if (!time) return null;

  const [hours, minutes = "0"] = time.split(":");
  const parsedHours = Number(hours);
  const parsedMinutes = Number(minutes);

  if (Number.isNaN(parsedHours) || Number.isNaN(parsedMinutes)) return null;

  return parsedHours * 60 + parsedMinutes;
};

export const getBookingsForTimeSlot = (
  bookings: Booking[],
  date: string,
  timeSlot: number,
): Booking[] => {
  const slotMinutes = Math.round(timeSlot * 60);

  return bookings.filter((booking) => {
    if (booking.date !== date) return false;

    const bookingStart = parseTimeToMinutes(booking.time);
    return bookingStart >= slotMinutes && bookingStart < slotMinutes + 30;
  });
};

export const getBookingsByPharmacist = (
  bookings: Booking[],
  pharmacist: string,
): Booking[] => {
  return bookings.filter((booking) => booking.pharmacist === pharmacist);
};

export const getBookingsByServiceType = (
  bookings: Booking[],
  serviceType: string,
): Booking[] => {
  return bookings.filter((booking) => booking.serviceType === serviceType);
};

export const getBookingsByStatus = (
  bookings: Booking[],
  status: string,
): Booking[] => {
  return bookings.filter((booking) => booking.status === status);
};

export const getTodaysBookings = (bookings: Booking[]): Booking[] => {
  const today = new Date().toISOString().split("T")[0];
  return getBookingsForDate(bookings, today);
};

export const getUpcomingBookings = (
  bookings: Booking[],
  days: number = 7,
): Booking[] => {
  const today = new Date();
  const futureDate = new Date(today.getTime() + days * 24 * 60 * 60 * 1000);

  return bookings.filter((booking) => {
    const bookingDate = new Date(booking.date);
    return bookingDate >= today && bookingDate <= futureDate;
  });
};

export const formatDate = (
  year: number,
  month: number,
  day: number,
): string => {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

export const formatDateFromDate = (date: Date): string => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

export interface BlockOutMultiDayInfo {
  isMultiDay: boolean;
  startDate: string;
  endDate: string;
  durationHours: number;
}

export const detectBlockOutMultiDayInfo = (
  blockOut: any,
): BlockOutMultiDayInfo => {
  if (!blockOut) {
    return { isMultiDay: false, startDate: "", endDate: "", durationHours: 0 };
  }

  let sDate = blockOut.startDate || blockOut.date || blockOut.StartDate || "";
  let eDate = blockOut.endDate || blockOut.date || blockOut.EndDate || sDate;

  if (typeof sDate === "string" && sDate.includes("T")) {
    sDate = sDate.split("T")[0];
  }
  if (typeof eDate === "string" && eDate.includes("T")) {
    eDate = eDate.split("T")[0];
  }

  let sTime = blockOut.startTime || "00:00";
  let eTime = blockOut.endTime || "23:59";

  if (
    blockOut.StartDate &&
    typeof blockOut.StartDate === "string" &&
    blockOut.StartDate.includes("T")
  ) {
    const parts = blockOut.StartDate.split("T")[1]?.substring(0, 5);
    if (parts) sTime = parts;
  }
  if (
    blockOut.EndDate &&
    typeof blockOut.EndDate === "string" &&
    blockOut.EndDate.includes("T")
  ) {
    const parts = blockOut.EndDate.split("T")[1]?.substring(0, 5);
    if (parts) eTime = parts;
  }

  let durationHours = 0;
  try {
    if (sDate && eDate) {
      const startDateTime = new Date(
        `${sDate}T${sTime.length === 5 ? sTime : "00:00"}`,
      );
      const endDateTime = new Date(
        `${eDate}T${eTime.length === 5 ? eTime : "23:59"}`,
      );
      const diffMs = endDateTime.getTime() - startDateTime.getTime();
      if (!isNaN(diffMs) && diffMs > 0) {
        durationHours = diffMs / (1000 * 60 * 60);
      }
    }
  } catch {
    durationHours = 0;
  }

  const isMultiDay = sDate !== eDate || durationHours >= 24;

  return {
    isMultiDay,
    startDate: sDate,
    endDate: eDate || sDate,
    durationHours,
  };
};

// Block-out utility functions
export const getBlockOutsForDate = (
  blockOuts: BlockOut[],
  date: string,
): BlockOut[] => {
  return (blockOuts || []).filter((blockOut) => {
    const sDate = blockOut.startDate || blockOut.date || "";
    const eDate = blockOut.endDate || blockOut.startDate || blockOut.date || "";
    if (!sDate) return false;
    return date >= sDate && date <= eDate;
  });
};

export const getBlockOutsForTimeSlot = (
  blockOuts: BlockOut[],
  date: string,
  timeSlot: number,
): BlockOut[] => {
  const slotStartMinutes = Math.round(timeSlot * 60);
  const slotEndMinutes = slotStartMinutes + 30;

  return (blockOuts || []).filter((blockOut) => {
    let sDate = blockOut.startDate || blockOut.date || "";
    let eDate = blockOut.endDate || blockOut.startDate || blockOut.date || "";
    if (typeof sDate === "string" && sDate.includes("T"))
      sDate = sDate.split("T")[0];
    if (typeof eDate === "string" && eDate.includes("T"))
      eDate = eDate.split("T")[0];

    if (!sDate) return false;
    if (date < sDate || date > eDate) return false;

    // All-day blockout check: render on the first slot of the day
    if (
      (blockOut as any).isAllDay ||
      (!blockOut.startTime && !blockOut.endTime)
    ) {
      return slotStartMinutes === 480 || timeSlot === 0 || timeSlot === 8;
    }

    let effectiveStartTime = blockOut.startTime || "00:00";
    let effectiveEndTime = blockOut.endTime || "23:59";

    const startMinutes = parseTimeToMinutes(effectiveStartTime);
    const endMinutes = parseTimeToMinutes(effectiveEndTime);

    if (startMinutes === null || endMinutes === null) return false;

    if (date === sDate) {
      if (startMinutes < 480) {
        // Start time is before 8:00 AM grid start, render on first visible slot
        return slotStartMinutes === 480 || timeSlot === 0 || timeSlot === 8;
      }
      // Render ONLY in the single time slot where the block-out STARTS
      return startMinutes >= slotStartMinutes && startMinutes < slotEndMinutes;
    } else {
      // Continuation of multi-day blockout: render on first slot of day
      return slotStartMinutes === 480 || timeSlot === 0 || timeSlot === 8;
    }
  });
};

// Task utility functions
export const getTasksForDate = (tasksList: Task[], date: string): Task[] => {
  return (tasksList || []).filter((task) => task.dueDate === date);
};

export const getTasksForTimeSlot = (
  tasksList: Task[],
  date: string,
  timeSlot: number,
): Task[] => {
  const slotStartMinutes = Math.round(timeSlot * 60);
  const slotEndMinutes = slotStartMinutes + 30;

  return (tasksList || []).filter((task) => {
    if (task.dueDate !== date) return false;
    if (!task.dueTime) return true; // Show on date if no specific time set

    const timeMinutes = parseTimeToMinutes(task.dueTime);
    if (timeMinutes === null) return true;

    return timeMinutes >= slotStartMinutes && timeMinutes < slotEndMinutes;
  });
};

export const isTimeSlotBlocked = (
  blockOuts: BlockOut[],
  date: string,
  time: string,
  room?: string,
  pharmacist?: string,
): boolean => {
  const timeHour = parseInt(time.split(":")[0]);
  const timeMinute = parseInt(time.split(":")[1]);

  return blockOuts.some((blockOut) => {
    if (blockOut.date !== date) return false;

    const startHour = parseInt(blockOut.startTime.split(":")[0]);
    const startMinute = parseInt(blockOut.startTime.split(":")[1]);
    const endHour = parseInt(blockOut.endTime.split(":")[0]);
    const endMinute = parseInt(blockOut.endTime.split(":")[1]);

    const startTimeMinutes = startHour * 60 + startMinute;
    const endTimeMinutes = endHour * 60 + endMinute;
    const currentTimeMinutes = timeHour * 60 + timeMinute;

    const isInTimeRange =
      currentTimeMinutes >= startTimeMinutes &&
      currentTimeMinutes < endTimeMinutes;

    if (!isInTimeRange) return false;

    // Check if room is affected
    if (room && blockOut.affectedRooms?.includes(room)) return true;

    // Check if pharmacist is affected
    if (pharmacist && blockOut.affectedPharmacists?.includes(pharmacist))
      return true;

    // If no specific room/pharmacist restrictions, it affects all
    if (
      !blockOut.affectedRooms?.length &&
      !blockOut.affectedPharmacists?.length
    )
      return true;

    return false;
  });
};

// Get all clinicians (pharmacists) from members
export const getClinicians = (): Member[] => {
  return initialMembers.filter((member) => member.isClinician && member.active);
};

// Group bookings by their date
export const groupBookingsByDay = (
  bookings: Booking[],
): Record<string, Booking[]> => {
  const grouped: Record<string, Booking[]> = {};
  bookings.forEach((booking) => {
    if (!grouped[booking.date]) {
      grouped[booking.date] = [];
    }
    grouped[booking.date].push(booking);
  });
  return grouped;
};

// Get short day name from date string (e.g., "2026-01-20" -> "Mon")
export const getDayShortName = (dateString: string): string => {
  const date = new Date(dateString);
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  return dayNames[date.getDay()];
};

// NEW: extra imports for the hook/state

// === NEW: helpers that used to live in schedule.tsx ===

// Service colors (exported so components can reuse if needed)
export const getServiceColors = (isDarkMode: boolean) => ({
  Earwax: isDarkMode
    ? "bg-yellow-900 text-yellow-200"
    : "bg-yellow-100 text-yellow-800",
  Travel: isDarkMode
    ? "bg-orange-900 text-orange-200"
    : "bg-orange-100 text-orange-800",
  Weight: isDarkMode
    ? "bg-green-900 text-green-200"
    : "bg-green-100 text-green-800",
  Covid: isDarkMode
    ? "bg-purple-900 text-purple-200"
    : "bg-purple-100 text-purple-800",
});

// Service accent colors for vertical bar indicators
export const getServiceAccentColor = (serviceType: string): string => {
  const colorMap: Record<string, string> = {
    Earwax: "bg-yellow-400",
    Travel: "bg-orange-400",
    Weight: "bg-green-400",
    Covid: "bg-purple-400",
  };
  return colorMap[serviceType] || "bg-gray-400";
};

// Block-out accent colors for vertical bar indicators
export const getBlockOutAccentColor = (blockOutType: string): string => {
  const colorMap: Record<string, string> = {
    team_event: "bg-indigo-500",
    training: "bg-green-500",
    maintenance: "bg-yellow-500",
    break: "bg-gray-500",
    other: "bg-purple-500",
  };
  return colorMap[blockOutType] || "bg-gray-500";
};

// Pre-computed time slots (08:00 to 23:30)
export const TIME_SLOTS = Array.from({ length: 16 }, (_, i) => i + 8); // 8 → 23 (16 hours)

export const FULL_DAY_TIME_SLOTS = Array.from(
  { length: 32 },
  (_, i) => 8 + i * 0.5,
); // 8.0 → 23.5 (08:00 to 23:30)

export type ModalState =
  | {
      isOpen: false;
      mode: null;
      booking?: Booking;
      initialDate?: string;
      initialTime?: string;
      initialPharmacist?: string;
      initialServiceType?: string;
      skipSlotSelection?: boolean;
    }
  | {
      isOpen: true;
      mode: "create";
      booking?: undefined;
      initialDate?: string;
      initialTime?: string;
      initialPharmacist?: string;
      initialServiceType?: string;
      skipSlotSelection?: boolean;
    }
  | {
      isOpen: true;
      mode: "edit";
      booking: Booking;
      initialDate?: undefined;
      initialTime?: undefined;
      initialPharmacist?: undefined;
      initialServiceType?: undefined;
      skipSlotSelection?: undefined;
    };

export type BlockOutModalState =
  | { isOpen: false; mode: null; blockOut?: BlockOut; initialDate?: string }
  | { isOpen: true; mode: "create"; blockOut?: undefined; initialDate?: string }
  | { isOpen: true; mode: "edit"; blockOut: BlockOut; initialDate?: undefined };

export interface UseScheduleDataReturn {
  // theme
  isDarkMode: boolean;
  currentPharmacyId: string;
  pharmacistNames: string[];
  isEventsLoading: boolean;
  // core state
  currentDate: Date;
  view: "month" | "week" | "day" | "threeday" | "workweek";
  calendarSelectedDate: Date;
  modalState: ModalState;
  blockOutModalState: BlockOutModalState;

  // data
  pharmacists: string[];
  roomNames: string[];
  blockOutTypes: typeof blockOutTypes;
  serviceTypes: any[];
  defaultPharmacyServices: any[];
  serviceTypesInfo: ServiceTypeInfo[];
  serviceColors: ReturnType<typeof getServiceColors>;
  serviceColorMap: Record<string, string>; // API service colors (hex)
  serviceNameMap: Record<string, string>; // Service ID to name mapping
  timeSlots: number[];
  dayNames: string[];
  members: Member[];
  clinicians: Member[];
  resources: any[];
  currentUserEmail: string;
  googleSyncEvents: any[];
  pharmacyRooms: any[];
  dynamicRooms: string[];
  dynamicResources: { id: string; name: string }[];

  // filters
  selectedPharmacists: string[];
  selectedServiceTypes: string[];
  selectedRooms: string[];
  selectedBlockOutTypes: string[];

  // filtered accessors
  getDaysInMonth: (date: Date) => (number | null)[];
  getWeekDays: (date: Date) => Date[];
  getThreeDays: (date: Date) => Date[];
  getWorkWeekDays: (date: Date) => Date[];
  getFilteredBookingsForDate: (date: string) => Booking[];
  getFilteredBookingsForTimeSlot: (date: string, hour: number) => Booking[];
  getFilteredBlockOutsForDate: (date: string) => BlockOut[];
  getFilteredBlockOutsForTimeSlot: (date: string, hour: number) => BlockOut[];
  filteredTasks: Task[];
  getFilteredTasksForDate: (date: string) => Task[];
  getFilteredTasksForTimeSlot: (date: string, hour: number) => Task[];
  handleStartConsultation: (b: any) => void;

  // utils
  formatDate: typeof formatDate;
  formatDateFromDate: typeof formatDateFromDate;
  getViewTitle: () => string;

  // actions (navigation / calendar)
  navigate: (dir: "prev" | "next") => void;
  onCalendarSelect: (date: Date | undefined) => void;
  handleViewDay: (dateString: string) => void;

  // actions (filters)
  handleServiceTypeChange: (serviceType: string, checked: boolean) => void;
  handlePharmacistChange: (pharmacist: string, checked: boolean) => void;
  handleSelectAllServices: (checked: boolean) => void;
  handleSelectAllPharmacists: (checked: boolean) => void;
  handleRoomChange: (room: string, checked: boolean) => void;
  handleSelectAllRooms: (checked: boolean) => void;
  handleBlockOutTypeChange: (type: string, checked: boolean) => void;
  handleSelectAllBlockOutTypes: (checked: boolean) => void;

  // actions (bookings + block outs)
  onCreateBooking: (bookingData: any) => void;
  onCreateBookingForDate: (dateString: string) => void;
  onCreateBookingWithDefaults: (options: {
    date: string;
    time?: string;
    pharmacist?: string;
    serviceType?: string;
  }) => void;
  onShowEditBooking: (bookingId: string, booking: Booking) => void;
  onEditBooking: (bookingId: string, bookingData: any) => void;
  onDeleteBooking: (bookingId: string) => void;

  onCreateBlockOut: (blockOutData: any) => void;
  onShowEditBlockOut: (blockOutId: string, blockOut: BlockOut) => void;
  onEditBlockOut: (blockOutId: string, blockOutData: any) => void;
  onDeleteBlockOut: (blockOutId: string) => void;

  // modal wiring
  openNewAppointment: () => void;
  closeModal: () => void;
  onSaveFromModal: (data: any) => void;
  closeBlockOutModal: () => void;
  onSaveFromBlockOutModal: (data: any) => void;
  setView: (v: "month" | "week" | "day" | "threeday" | "workweek") => void;
  allUserWeekAvailability: any[];
  inviteMemberLogs: any[];
}

export function useScheduleData(): UseScheduleDataReturn {
  const routerNavigate = useNavigate();
  const { appState, scheduleEventState, pharmacyServiceState } = useTitanApp();
  const {
    darkMode,
    currentPharmacyId,
    companyId,
    features,
    currentUser,
  } = appState || {};
  const { ifPharmacyDashboardEnabled } = features || {};
  const {
    scheduleEvents = [],
    resources = [],
    isEventsLoading,
    googleSyncEvents,
    allUserWeekAvailability = [],
    inviteMemberLogs = [],
  } = scheduleEventState || {};

  const {
    defaultPharmacyServices = [],
  } = pharmacyServiceState || {};

  // Extract dynamic data from API
  const dynamicPharmacists = useMemo(
    () => extractPharmacistsFromResources(resources),
    [resources],
  );
  const dynamicResources = useMemo(
    () => extractPharmacists(resources),
    [resources],
  );

  const getRoomResources = (resources: any[]) => {
    return resources.filter((resource) => resource.resourceType === 2);
  };
  const roomInventory = getRoomResources(resources);

  const dynamicRooms = useMemo(
    () => extractRoomsFromResources(resources),
    [resources],
  );

  const dynamicServices = useMemo(
    () => extractServiceTypesFromServices(defaultPharmacyServices),
    [defaultPharmacyServices],
  );

  // Create service types info with colors (same as Scheduler)
  const serviceTypesInfo = useMemo(
    () => extractServiceTypesInfo(defaultPharmacyServices),
    [defaultPharmacyServices],
  );

  // Create service color map from API data
  const serviceColorMap = useMemo(
    () => createServiceColorMap(defaultPharmacyServices),
    [defaultPharmacyServices],
  );

  // Create service name map from service ID
  const serviceNameMap = useMemo(
    () => createServiceNameMap(defaultPharmacyServices),
    [defaultPharmacyServices],
  );

  const pharmacistNames = dynamicPharmacists;

  // Get current user's resource email for auto-filter
  const currentUserEmail = (currentUser as any)?.emailAddress || "";
  const currentUserResourceName = useMemo(() => {
    if (!currentUserEmail || !resources || resources.length === 0) return "";
    const userResource = resources.find(
      (r: any) => r.id === currentUserEmail && r.resourceType === 1,
    );
    return userResource?.name || "";
  }, [currentUserEmail, resources]);

  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<
    "month" | "week" | "day" | "threeday" | "workweek"
  >("workweek");
  const [calendarSelectedDate, setCalendarSelectedDate] = useState(new Date());

  // Auto-select current user's resource by default
  const [selectedPharmacists, setSelectedPharmacists] = useState<string[]>(
    () => {
      return currentUserResourceName ? [currentUserResourceName] : [];
    },
  );

  // Sync selected pharmacists when current user resource is determined
  useEffect(() => {
    if (currentUserResourceName && selectedPharmacists.length === 0) {
      setSelectedPharmacists([currentUserResourceName]);
    }
  }, [currentUserResourceName]);
  const [selectedServiceTypes, setSelectedServiceTypes] = useState<string[]>(
    [],
  );
  const [selectedRooms, setSelectedRooms] = useState<string[]>([]);
  const [selectedBlockOutTypes, setSelectedBlockOutTypes] = useState<string[]>(
    [],
  );

  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    mode: null,
  });
  const [blockOutModalState, setBlockOutModalState] =
    useState<BlockOutModalState>({
      isOpen: false,
      mode: null,
    });

  // Transform API events to bookings (remote data) - ONLY use API data, no mock fallback
  const remoteBookings = useMemo(() => {
    if (scheduleEvents && scheduleEvents.length > 0) {
      return transformAPIEventsToBookings(
        scheduleEvents,
        resources,
        defaultPharmacyServices,
      );
    }
    // No fallback to mock data - return empty array if no API data available
    return [];
  }, [scheduleEvents, resources, defaultPharmacyServices]);

  // Transform API events to block-outs (filter non-appointment events)
  const remoteBlockOuts = useMemo(() => {
    if (scheduleEvents && scheduleEvents.length > 0) {
      return transformAPIEventsToBlockOuts(scheduleEvents, resources);
    }
    // Return empty array if no API data available
    return [];
  }, [scheduleEvents, resources]);

  // Transform API events to tasks (ScheduleEventType = 7)
  const remoteTasks = useMemo(() => {
    if (scheduleEvents && scheduleEvents.length > 0) {
      return transformAPIEventsToTasks(scheduleEvents, resources);
    }
    return [];
  }, [scheduleEvents, resources]);

  // Derive bookings strictly from remoteBookings (API response data, no local state duplication)
  const bookings = useMemo(() => {
    const list = [...remoteBookings];
    list.sort((a, b) => {
      const dateCompare = a.date.localeCompare(b.date);
      if (dateCompare !== 0) return dateCompare;
      return a.time.localeCompare(b.time);
    });
    return list;
  }, [remoteBookings]);

  // Derive block-outs strictly from remoteBlockOuts (API response data, no local state duplication)
  const blockOuts = useMemo(() => {
    const list = [...remoteBlockOuts];
    list.sort((a, b) => {
      const dateCompare = a.date.localeCompare(b.date);
      if (dateCompare !== 0) return dateCompare;
      return a.startTime.localeCompare(b.startTime);
    });
    return list;
  }, [remoteBlockOuts]);

  const roomNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    (resources || []).forEach((r: any) => {
      if (r.id && r.name) {
        map[String(r.id)] = r.name;
        map[r.name] = r.name;
      }
    });
    return map;
  }, [resources]);

  // filters
  const filteredBookings = useMemo(() => {
    return bookings.filter((b) => {
      const pharmacistMatch =
        selectedPharmacists.length === 0 ||
        selectedPharmacists.includes(b.pharmacist);
      // Use serviceId or serviceName for filtering
      const bServiceId = String(
        b.serviceId || b.serviceID || (b as any).ServiceID || (b as any).ClinicalServiceID || "",
      );
      const serviceName = b.serviceId
        ? serviceNameMap[b.serviceId]
        : b.serviceType;
      const serviceMatch =
        selectedServiceTypes.length === 0 ||
        (bServiceId && selectedServiceTypes.includes(bServiceId)) ||
        selectedServiceTypes.includes(serviceName);

      let roomMatch = selectedRooms.length === 0;
      if (!roomMatch) {
        const bRoom = b.room || (b as any).roomId;
        const bRoomId = (b as any).roomId || b.room;
        const resolvedRoomName =
          roomNameMap[String(bRoomId)] || roomNameMap[String(bRoom)] || bRoom;

        roomMatch = selectedRooms.some((selectedRoom) => {
          const normSelected = String(selectedRoom).trim().toLowerCase();
          return (
            normSelected === String(bRoom).trim().toLowerCase() ||
            normSelected === String(bRoomId).trim().toLowerCase() ||
            normSelected === String(resolvedRoomName).trim().toLowerCase()
          );
        });
      }

      return pharmacistMatch && serviceMatch && roomMatch;
    });
  }, [
    bookings,
    selectedPharmacists,
    selectedServiceTypes,
    selectedRooms,
    serviceNameMap,
    roomNameMap,
  ]);

  const filteredBlockOuts = useMemo(() => {
    return blockOuts.filter((bo) => {
      const typeMatch =
        selectedBlockOutTypes.length === 0 ||
        selectedBlockOutTypes.includes(bo.type);

      if (!typeMatch) return false;

      const hasAffectedPharmacists =
        bo.affectedPharmacists && bo.affectedPharmacists.length > 0;
      const hasAffectedRooms =
        bo.affectedRooms && bo.affectedRooms.length > 0;

      // Pharmacist Filter
      let pharmacistMatch = true;
      if (selectedPharmacists.length > 0) {
        if (hasAffectedPharmacists) {
          pharmacistMatch = bo.affectedPharmacists.some((p) => {
            const normP = String(p).trim().toLowerCase();
            return selectedPharmacists.some((sp) => {
              const normSp = String(sp).trim().toLowerCase();
              const res = (resources || []).find(
                (r: any) => String(r.name).trim().toLowerCase() === normSp
              );
              const spId = res ? String(res.id).trim().toLowerCase() : normSp;
              return normP === normSp || normP === spId;
            });
          });
        } else if (hasAffectedRooms) {
          // Room-specific blockout without explicit pharmacists applies to room regardless of pharmacist filter
          pharmacistMatch = true;
        }
      }

      // Room Filter
      let roomMatch = true;
      if (selectedRooms.length > 0) {
        if (hasAffectedRooms) {
          roomMatch = bo.affectedRooms.some((r) => {
            const rName = roomNameMap[String(r)] || r;
            return selectedRooms.some((sr) => {
              const normSr = String(sr).trim().toLowerCase();
              return (
                normSr === String(r).trim().toLowerCase() ||
                normSr === String(rName).trim().toLowerCase()
              );
            });
          });
        } else if (hasAffectedPharmacists) {
          // Pharmacist-specific blockout without explicit rooms applies to pharmacist regardless of room filter
          roomMatch = true;
        }
      }

      return pharmacistMatch && roomMatch;
    });
  }, [
    blockOuts,
    selectedBlockOutTypes,
    selectedPharmacists,
    selectedRooms,
    resources,
    roomNameMap,
  ]);

  const filteredTasks = useMemo(() => {
    return remoteTasks.filter((task) => {
      // 1. Pharmacist Filter
      let pharmacistMatch = true;
      if (selectedPharmacists && selectedPharmacists.length > 0) {
        if (
          !task.assignedTo ||
          task.assignedTo === "Unassigned" ||
          task.assignedTo === ""
        ) {
          pharmacistMatch = true;
        } else {
          const normAssigned = (task.assignedTo || "").trim().toLowerCase();
          pharmacistMatch = selectedPharmacists.some((pName) => {
            const normP = (pName || "").trim().toLowerCase();
            if (normAssigned === normP) return true;
            const res = (resources || []).find(
              (r: any) => r.name && r.name.trim().toLowerCase() === normP,
            );
            return (
              res &&
              String(res.id || "")
                .trim()
                .toLowerCase() === normAssigned
            );
          });
        }
      }

      // 2. Room Filter
      let roomMatch = selectedRooms.length === 0;
      if (!roomMatch) {
        const taskRoom = (task as any).room || (task as any).roomId || (task as any).affectedRooms;
        if (taskRoom) {
          const roomsArray = Array.isArray(taskRoom) ? taskRoom : [taskRoom];
          roomMatch = roomsArray.some((r: string) => {
            const rName = roomNameMap[String(r)] || r;
            return selectedRooms.some((sr) => {
              const normSr = String(sr).trim().toLowerCase();
              return (
                normSr === String(r).trim().toLowerCase() ||
                normSr === String(rName).trim().toLowerCase()
              );
            });
          });
        }
      }

      return pharmacistMatch && roomMatch;
    });
  }, [remoteTasks, selectedPharmacists, selectedRooms, resources, roomNameMap]);

  // helpers for filtered access
  const getFilteredBookingsForDate = (date: string) =>
    getBookingsForDate(filteredBookings, date);

  const getFilteredBookingsForTimeSlot = (date: string, hour: number) =>
    getBookingsForTimeSlot(filteredBookings, date, hour);

  const getFilteredBlockOutsForDate = (date: string) => {
    const result = getBlockOutsForDate(filteredBlockOuts, date);
    return result;
  };

  const getFilteredBlockOutsForTimeSlot = (date: string, hour: number) => {
    const result = getBlockOutsForTimeSlot(filteredBlockOuts, date, hour);
    return result;
  };

  const getFilteredTasksForDate = (date: string) =>
    getTasksForDate(filteredTasks, date);

  const getFilteredTasksForTimeSlot = (date: string, hour: number) =>
    getTasksForTimeSlot(filteredTasks, date, hour);

  const handleStartConsultation = async (b: any) => {
    const consultationCheckModel = {
      consultationID: b?.consultationID,
      patientId: b?.patientId || b?.patientID,
      id: b?.id,
    };

    await checkAndAddConsultation(consultationCheckModel);
    const consultationResAtStart =
      await getPatientConsultationByConsultationId(b?.consultationID);
    const pid = b?.patientId || b?.patientID;
    const consultationID = b?.consultationID || b?.id;
    const targetConsultationIdForDrafts = consultationID;

    const existingActionsAtStart = getConsultationActions(consultationResAtStart);
    const hasVaccineDraftAtStart = hasDraftAction(
      existingActionsAtStart,
      "CreateVaccineDraft",
      "12",
    );
    const hasTaskDraftAtStart = hasDraftAction(
      existingActionsAtStart,
      "CreateTask",
      "14",
    );

    const startTasks = Array.isArray(b?.tasksToComplete)
      ? b.tasksToComplete
      : [];
    const startVaccines = Array.isArray(b?.vaccinesAdminister)
      ? b.vaccinesAdminister
      : Array.isArray(b?.vaccinesToAdminister)
        ? b.vaccinesToAdminister
        : [];

    // If user starts directly from modal without update, persist draft actions here.
    if (
      targetConsultationIdForDrafts &&
      (startTasks.length > 0 || startVaccines.length > 0)
    ) {
      const chatMessages: any[] = [];

      if (startVaccines.length > 0 && !hasVaccineDraftAtStart) {
        const vaccineChat = await createVaccineDraftFromSchedule(
          startVaccines,
          targetConsultationIdForDrafts,
        );
        if (vaccineChat) chatMessages.push(vaccineChat);
      }

      if (startTasks.length > 0 && !hasTaskDraftAtStart) {
        const taskChat = await createTaskDraftFromSchedule(
          startTasks,
          targetConsultationIdForDrafts,
        );
        if (taskChat) chatMessages.push(taskChat);
      }

      if (chatMessages.length) {
        await SaveAIChatMessage({
          consultationChat: JSON.stringify(chatMessages),
          consultationId: targetConsultationIdForDrafts,
        });
      }
    }

    const targetPharmacyId =
      String(b?.otherPharmacyId || "").trim() || currentPharmacyId;
    if (pid && targetPharmacyId && consultationID) {
      await getPatientByPatientId(pid);
      setRoutedFromScheduler(true);
      setCurrentGlobalTabValue(ifPharmacyDashboardEnabled ? 4 : 3);
      setCurrentGlobalTabText("Patients");
      closeModal();
      setisPatientSearched(true);
      setPatientInfoTab(1);
      setShowNHS111ReferralTab(false);
      setIsTemplateSelected(true);
      const patientConsultation = await getPatientConsultations(pid);
      const consultationRes =
        await getPatientConsultationByConsultationId(consultationID);
      setPatientTabSwitcher(consultationRes?.preConsultationTemplate ? 1 : 0);
      await getPaymentAuditByConsultation({ consultationID: consultationID });
      if (consultationRes?.preConsultationTemplate) {
        const preConsultDataRes = await getPreConsultationByConsultationId({
          consultationId: consultationRes?.consultationId,
        });

        setPreConsultationTemplate(preConsultDataRes?.preConsultationTemplate);

        if (preConsultDataRes?.preConsultationTemplate) {
          let preConsultTranscription = "";

          preConsultDataRes.preConsultationTemplate.templateSections?.forEach(
            (section) => {
              section?.userControls?.forEach((control) => {
                if (control.label && control.value) {
                  preConsultTranscription += ` ${control.label} ${control.value} `;
                }
              });
            },
          );

          setPreConsultationTranscription(preConsultTranscription.trim());
        } else {
          setPreConsultationTranscription("");
        }
      } else {
        setPreConsultationTemplate(null);
        setPreConsultationTranscription("");
      }
      const data = {
        consultationId: consultationCheckModel.consultationID,
        actionStatus: 2,
      };

      const eventData = {
        id: consultationCheckModel.id,
        eventStatus: 3,
      };

      await updateConsultationStatus(data);
      await updateEventStatus(eventData);

      routerNavigate(
        `/pharmacy/patientconsultation/${targetPharmacyId}/${pid}/${consultationID}`,
      );
    }
  };

  // UI helpers
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: (number | null)[] = [];
    for (let i = 0; i < startingDayOfWeek; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);
    return days;
  };

  const getWeekDays = (date: Date) => {
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day;
    startOfWeek.setDate(diff);

    const weekDays: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      weekDays.push(d);
    }
    return weekDays;
  };

  const getThreeDays = (date: Date) => {
    // Get 3 days: current date and 2 days after
    const threeDays: Date[] = [];
    for (let i = 0; i < 3; i++) {
      const d = new Date(date);
      d.setDate(date.getDate() + i);
      threeDays.push(d);
    }
    return threeDays;
  };

  const getWorkWeekDays = (date: Date) => {
    // Get Monday-Friday of the week containing the date
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    // Calculate offset to Monday (day 1)
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
    startOfWeek.setDate(diff);

    const workWeekDays: Date[] = [];
    for (let i = 0; i < 5; i++) {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      workWeekDays.push(d);
    }
    return workWeekDays;
  };

  const navigate = (direction: "prev" | "next") => {
    setCurrentDate((prev) => {
      const nd = new Date(prev);
      if (view === "month") {
        nd.setMonth(prev.getMonth() + (direction === "prev" ? -1 : 1));
      } else if (view === "week") {
        nd.setDate(prev.getDate() + (direction === "prev" ? -7 : 7));
      } else if (view === "workweek") {
        nd.setDate(prev.getDate() + (direction === "prev" ? -7 : 7));
      } else if (view === "threeday") {
        nd.setDate(prev.getDate() + (direction === "prev" ? -3 : 3));
      } else {
        // day view
        nd.setDate(prev.getDate() + (direction === "prev" ? -1 : 1));
      }
      return nd;
    });
  };

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const getViewTitle = () => {
    if (view === "month") {
      return `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    }
    if (view === "week") {
      const weekDays = getWeekDays(currentDate);
      const start = weekDays[0];
      const end = weekDays[6];
      if (start.getMonth() === end.getMonth()) {
        return `${monthNames[start.getMonth()]} ${start.getDate()}-${end.getDate()}, ${start.getFullYear()}`;
      }
      return `${monthNames[start.getMonth()]} ${start.getDate()} - ${monthNames[end.getMonth()]} ${end.getDate()}, ${start.getFullYear()}`;
    }
    if (view === "workweek") {
      const workWeekDays = getWorkWeekDays(currentDate);
      const start = workWeekDays[0];
      const end = workWeekDays[4];
      if (start.getMonth() === end.getMonth()) {
        return `${monthNames[start.getMonth()]} ${start.getDate()}-${end.getDate()}, ${start.getFullYear()}`;
      }
      return `${monthNames[start.getMonth()]} ${start.getDate()} - ${monthNames[end.getMonth()]} ${end.getDate()}, ${start.getFullYear()}`;
    }
    if (view === "threeday") {
      const threeDays = getThreeDays(currentDate);
      const start = threeDays[0];
      const end = threeDays[2];
      if (start.getMonth() === end.getMonth()) {
        return `${monthNames[start.getMonth()]} ${start.getDate()}-${end.getDate()}, ${start.getFullYear()}`;
      }
      return `${monthNames[start.getMonth()]} ${start.getDate()} - ${monthNames[end.getMonth()]} ${end.getDate()}, ${start.getFullYear()}`;
    }
    return `${monthNames[currentDate.getMonth()]} ${currentDate.getDate()}, ${currentDate.getFullYear()}`;
  };

  // actions (calendar/select)
  const onCalendarSelect = (date?: Date) => {
    if (!date) return;
    setCalendarSelectedDate(date);
    setCurrentDate(date);
    setView("day");
  };

  const handleViewDay = (dateString: string) => {
    setCurrentDate(new Date(dateString));
    setView("day");
  };

  // actions (filters)
  const handleServiceTypeChange = (serviceType: string, checked: boolean) => {
    setSelectedServiceTypes((prev) =>
      checked ? [...prev, serviceType] : prev.filter((t) => t !== serviceType),
    );
  };

  const handlePharmacistChange = (pharmacist: string, checked: boolean) => {
    setSelectedPharmacists((prev) =>
      checked ? [...prev, pharmacist] : prev.filter((p) => p !== pharmacist),
    );
  };

  const handleSelectAllServices = (checked: boolean) =>
    setSelectedServiceTypes(
      checked
        ? serviceTypesInfo.map((s) => s._id || s.name).filter(Boolean)
        : [],
    );

  const handleSelectAllPharmacists = (checked: boolean) =>
    setSelectedPharmacists(checked ? [...pharmacistNames] : []);

  const handleRoomChange = (room: string, checked: boolean) => {
    setSelectedRooms((prev) =>
      checked ? [...prev, room] : prev.filter((r) => r !== room),
    );
  };

  const handleSelectAllRooms = (checked: boolean) =>
    setSelectedRooms(checked ? [...dynamicRooms] : []);

  const handleBlockOutTypeChange = (type: string, checked: boolean) =>
    setSelectedBlockOutTypes((prev) =>
      checked ? [...prev, type] : prev.filter((t) => t !== type),
    );

  const handleSelectAllBlockOutTypes = (checked: boolean) =>
    setSelectedBlockOutTypes(checked ? blockOutTypes.map((t) => t.value) : []);

  const formatScheduleDateTime = (dateStr: string, timeStr: string): string => {
    const date = new Date(`${dateStr}T${timeStr}`);

    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}T${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}:00Z`;
  };

  const getConsultationEntity = (consultationRes: any): any => {
    const root = Array.isArray(consultationRes)
      ? consultationRes[0]
      : consultationRes;
    const dataNode = root?.data || root?.Data;

    if (Array.isArray(dataNode)) {
      return dataNode[0] || root || {};
    }

    return dataNode || root || {};
  };

  const getConsultationActions = (consultationRes: any): any[] => {
    const entity = getConsultationEntity(consultationRes);
    const directActions = entity?.actions || entity?.Actions;

    if (Array.isArray(directActions)) return directActions;

    const nestedDataActions = entity?.data?.actions || entity?.data?.Actions;
    if (Array.isArray(nestedDataActions)) return nestedDataActions;

    return [];
  };

  const hasDraftAction = (
    actions: any[],
    actionName: string,
    actionNumber: string,
  ): boolean => {
    const targetName = String(actionName).trim().toLowerCase();
    const targetNumber = String(actionNumber).trim();

    return (actions || []).some((action) => {
      const existingName = String(action?.actionName || action?.ActionName || "")
        .trim()
        .toLowerCase();
      const existingNumber = String(
        action?.actionNumber || action?.ActionNumber || "",
      ).trim();

      return existingNumber === targetNumber || existingName === targetName;
    });
  };

  const createVaccineDraftFromSchedule = async (
    vaccines: any[],
    targetConsultationId: string,
  ) => {
    const consultationRes =
      await getPatientConsultationByConsultationId(targetConsultationId);
    const actions = getConsultationActions(consultationRes);

    if (hasDraftAction(actions, "CreateVaccineDraft", "12")) {
      return null;
    }

    const draftData = {
      vaccineItems: vaccines.map((v) => ({
        vaccineName: v?.vaccineName || v?.name || "",
        dose: v?.dose || "",
        schedule: v?.schedule || "As per schedule",
        notes: v?.notes || "",
        route: v?.route || "IM / SC",
        site: v?.site || "Deltoid - left / right",
      })),
      timestamp: new Date().toISOString(),
    };

    const payload = {
      payload: JSON.stringify(draftData),
      actionName: "CreateVaccineDraft",
      actionNumber: "12",
      isPerformed: true,
      performedAt: new Date().toISOString(),
      draftStatus: "pending",
    };

    await updateConsultationActions({
      consultationId: targetConsultationId,
      payload,
    });

    return {
      sender: "success",
      message: "Vaccine draft created.",
      actionId: 12,
      details: payload,
    };
  };

  const createTaskDraftFromSchedule = async (
    tasks: any[],
    targetConsultationId: string,
  ) => {
    const consultationRes =
      await getPatientConsultationByConsultationId(targetConsultationId);
    const actions = getConsultationActions(consultationRes);

    if (hasDraftAction(actions, "CreateTask", "14")) {
      return null;
    }

    const taskLabels = tasks
      .map((t) => (typeof t === "string" ? t : t?.label || t?.text || ""))
      .filter(Boolean);

    const draftData = {
      tasks: taskLabels.map((label) => ({
        label,
        completed: false,
      })),
      timestamp: new Date().toISOString(),
    };

    const payload = {
      payload: JSON.stringify(draftData),
      actionName: "CreateTask",
      actionNumber: "14",
      isPerformed: true,
      performedAt: new Date().toISOString(),
      draftStatus: "pending",
    };

    await updateConsultationActions({
      consultationId: targetConsultationId,
      payload,
    });

    return {
      sender: "success",
      message: "Task created.",
      actionId: 14,
      details: payload,
    };
  };

  const persistDraftActionsForAppointment = async (appointment: any) => {
    const targetConsultationId =
      appointment?.consultationID || appointment?.id;

    if (!targetConsultationId) return;

    const chatMessages: any[] = [];

    if (appointment?.vaccinesAdminister?.length) {
      const vaccineChat = await createVaccineDraftFromSchedule(
        appointment.vaccinesAdminister,
        targetConsultationId,
      );
      if (vaccineChat) chatMessages.push(vaccineChat);
    }

    if (appointment?.tasksToComplete?.length) {
      const taskChat = await createTaskDraftFromSchedule(
        appointment.tasksToComplete,
        targetConsultationId,
      );
      if (taskChat) chatMessages.push(taskChat);
    }

    if (chatMessages.length) {
      await SaveAIChatMessage({
        consultationChat: JSON.stringify(chatMessages),
        consultationId: targetConsultationId,
      });
    }
  };

  // actions (bookings)
  const onCreateBooking = useCallback(
    async (bookingData: any) => {
      try {
        const newId = uuidv4();
        const { date, time, duration } = bookingData;
        // Start DateTime (+1 hour)
        const start = new Date(`${date}T${time}:00`);
        start.setHours(start.getHours());

        const startDate = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}-${String(start.getDate()).padStart(2, "0")}T${String(start.getHours()).padStart(2, "0")}:${String(start.getMinutes()).padStart(2, "0")}:00Z`;

        const end = new Date(start);
        end.setMinutes(end.getMinutes() + Number(duration));

        const endDate = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, "0")}-${String(end.getDate()).padStart(2, "0")}T${String(end.getHours()).padStart(2, "0")}:${String(end.getMinutes()).padStart(2, "0")}:00Z`;

        const appointment = {
          id: newId,
          consultationID: newId,
          startDate: startDate,
          endDate: endDate,
          duration: parseInt(bookingData.duration),
          durationUnit: "m",
          cls: "",
          patientID: bookingData.patientId || "",
          name: bookingData.notes || "",
          recurrenceRule: "",
          exceptionDates: [],
          resourceId: bookingData.pharmacist,
          allDay: false,
          scheduleEventType: 1,
          serviceID: bookingData.serviceId || bookingData.serviceType,
          email: bookingData.email || "",
          mobile: bookingData.mobile || "",
          confirmationMethod: bookingData.confirmationMethod,
          RoomId: bookingData.room || "",
          createdAt: new Date().toISOString(),
          ...(bookingData.tasksToComplete?.length && {
            tasksToComplete: bookingData.tasksToComplete,
          }),
          ...(bookingData.vaccinesAdminister?.length && {
            vaccinesAdminister: bookingData.vaccinesAdminister,
          }),
          ...(bookingData.pharmacist === "any" && {
            companyId: companyId,
          }),
        };

          await addScheduleEvent(appointment, currentPharmacyId);
          await persistDraftActionsForAppointment(appointment);
        await refreshScheduleEventsSilently(currentPharmacyId);
        setModalState({ isOpen: false, mode: null });
      } catch (e) {
        console.error("Failed to create booking:", e);
        throw e;
      }
    },
    [currentPharmacyId],
  );

  const onShowEditBooking = useCallback(
    (bookingId: string, booking: Booking) => {
      setModalState({ isOpen: true, mode: "edit", booking });
    },
    [],
  );

  const onEditBooking = useCallback(
    async (bookingId: string, bookingData: any) => {
      try {
        const { date, time, duration } = bookingData;

        // Start DateTime (+1 hour)
        const start = new Date(`${date}T${time}:00`);
        start.setHours(start.getHours());

        const startDate = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}-${String(start.getDate()).padStart(2, "0")}T${String(start.getHours()).padStart(2, "0")}:${String(start.getMinutes()).padStart(2, "0")}:00Z`;

        // End DateTime (+duration after updated start time)
        const end = new Date(start);
        end.setMinutes(end.getMinutes() + Number(duration));

        const endDate = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, "0")}-${String(end.getDate()).padStart(2, "0")}T${String(end.getHours()).padStart(2, "0")}:${String(end.getMinutes()).padStart(2, "0")}:00Z`;


        const existingForEdit = bookings.find(
          (b) =>
            String(b.id) === String(bookingId) ||
            String(b.consultationID) === String(bookingId),
        );

        const appointment = {
          id: bookingId,
          consultationID: existingForEdit?.consultationID || bookingId,
          startDate: startDate,
          endDate: endDate,
          duration: parseInt(bookingData.duration),
          durationUnit: "m",
          cls: "",
          patientID: bookingData.patientId || "",
          name: bookingData.notes || "",
          recurrenceRule: "",
          exceptionDates: [],
          resourceId: bookingData.pharmacist,
          allDay: false,
          scheduleEventType: 1,
          serviceID: bookingData.serviceId || bookingData.serviceType,
          email: bookingData.email || "",
          mobile: bookingData.mobile || "",
          confirmationMethod: bookingData.confirmationMethod,
          RoomId: bookingData.room || "",
          createdAt: new Date().toISOString(),
          ...(bookingData.tasksToComplete?.length && {
            tasksToComplete: bookingData.tasksToComplete,
          }),
          ...(bookingData.vaccinesAdminister?.length && {
            vaccinesAdminister: bookingData.vaccinesAdminister,
          }),
          ...(bookingData.pharmacist === "any" && {
            companyId: companyId,
          }),
        };

          await addScheduleEvent(appointment, currentPharmacyId);
          await persistDraftActionsForAppointment(appointment);
        await refreshScheduleEventsSilently(currentPharmacyId);
        setModalState({ isOpen: false, mode: null });
      } catch (e) {
        console.error("Failed to update booking:", e);
        throw e;
      }
    },
    [currentPharmacyId, bookings],
  );

  const onDeleteBooking = async (bookingId: string) => {
    try {
      await deleteScheduleEvents([bookingId], currentPharmacyId);
      await refreshScheduleEventsSilently(currentPharmacyId);
    } catch (e) {
      console.error("Failed to delete booking:", e);
      throw e;
    }
  };

  const onCreateBookingForDate = useCallback((dateString: string) => {
    setModalState({ isOpen: true, mode: "create", initialDate: dateString });
  }, []);

  const onCreateBookingWithDefaults = useCallback(
    (options: {
      date: string;
      time?: string;
      pharmacist?: string;
      serviceType?: string;
    }) => {
      setModalState({
        isOpen: true,
        mode: "create",
        initialDate: options.date,
        initialTime: options.time,
        initialPharmacist: options.pharmacist,
        initialServiceType: options.serviceType,
      });
    },
    [],
  );

  const openNewAppointment = useCallback(() => {
    setModalState({ isOpen: true, mode: "create", skipSlotSelection: true });
  }, []);

  // helpers for block-out type mapping
  const mapBlockOutTypeToEnum = (type: string): number => {
    const t = (type || "").toLowerCase();
    switch (t) {
      case "team_event":
      case "teamevent":
        return 2; // TeamEvent
      case "training":
      case "trainingsession":
        return 3; // TrainingSession
      case "maintenance":
        return 4; // Maintenance
      case "break":
        return 5; // Break
      default:
        return 6; // Other
    }
  };

  // actions (block outs)
  const onCreateBlockOut = async (blockOutData: any) => {
    try {
      const newId = uuidv4();
      const startDateStr = blockOutData.startDate || blockOutData.date;
      const endDateStr = blockOutData.endDate || blockOutData.date;
      const startTimeStr = blockOutData.startTime === "00:00" ? "08:00" : blockOutData.startTime || "08:00";
      const endTimeStr = blockOutData.endTime || "23:59";
      const startDate = formatScheduleDateTime(startDateStr, startTimeStr);
      const endDate = formatScheduleDateTime(endDateStr, endTimeStr);
      const payload = {
        id: newId,
        title: blockOutData.title || "",
        type: mapBlockOutTypeToEnum(blockOutData.type),
        startDate,
        endDate,
        description: blockOutData.description || "",
        affectedRooms: blockOutData.affectedRooms || [],
        affectedPharmacists: blockOutData.affectedPharmacists || [],
      };
      await addBlockOut(payload, currentPharmacyId);
      await refreshScheduleEventsSilently(currentPharmacyId);
    } catch (e) {
      console.error("Failed to create block-out:", e);
      throw e;
    }
  };

  const onShowEditBlockOut = useCallback(
    (blockOutId: string, blockOut: BlockOut) => {
      setBlockOutModalState({ isOpen: true, mode: "edit", blockOut });
    },
    [],
  );

  const onEditBlockOut = async (blockOutId: string, blockOutData: any) => {
    try {
      const startDateStr = blockOutData.startDate || blockOutData.date;
      const endDateStr = blockOutData.endDate || blockOutData.date;
      const startTimeStr = blockOutData.startTime || "08:00";
      const endTimeStr = blockOutData.endTime || "23:59";

      const startDate = formatScheduleDateTime(startDateStr, startTimeStr);
      const endDate = formatScheduleDateTime(endDateStr, endTimeStr);

      const payload = {
        id: blockOutId,
        title: blockOutData.title || "",
        type: mapBlockOutTypeToEnum(blockOutData.type),
        startDate,
        endDate,
        description: blockOutData.description,
        affectedRooms: blockOutData.affectedRooms || [],
        affectedPharmacists: blockOutData.affectedPharmacists || [],
        color: blockOutData.color || "",
      };

      await addBlockOut(payload, currentPharmacyId);
      await refreshScheduleEventsSilently(currentPharmacyId);
    } catch (e) {
      console.error("Failed to update block-out:", e);
      throw e;
    }
  };

  const onDeleteBlockOut = async (blockOutId: string) => {
    try {
      const pharmacyId = currentPharmacyId;
      await deleteBlockOut(blockOutId, pharmacyId);
      await refreshScheduleEventsSilently(currentPharmacyId);
    } catch (e) {
      console.error("Failed to delete block-out:", e);
      throw e;
    }
  };

  // modal helpers
  const closeModal = () => setModalState({ isOpen: false, mode: null });

  const onSaveFromModal = async (data: any) => {
    try {
      if (
        modalState.isOpen &&
        modalState.mode === "edit" &&
        modalState.booking
      ) {
        await onEditBooking(modalState.booking.id, data);
      } else {
        await onCreateBooking(data);
      }
    } catch (e) {
      console.error("Failed to save booking:", e);
      throw e;
    }
  };

  // block out modal helpers
  const closeBlockOutModal = () =>
    setBlockOutModalState({ isOpen: false, mode: null });

  const onSaveFromBlockOutModal = async (data: any) => {
    try {
      if (
        blockOutModalState.isOpen &&
        blockOutModalState.mode === "edit" &&
        blockOutModalState.blockOut
      ) {
        await onEditBlockOut(blockOutModalState.blockOut.id, data);
      } else {
        await onCreateBlockOut(data);
      }
      closeBlockOutModal();
    } catch (e) {
      console.error("Failed to save block-out:", e);
      throw e;
    }
  };

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const members = initialMembers;
  const clinicians = getClinicians();

  return {
    // theme
    isDarkMode: darkMode,
    currentPharmacyId,
    isEventsLoading,
    // core state
    currentDate,
    view,
    calendarSelectedDate,
    modalState,
    blockOutModalState,
    pharmacistNames,
    // data - ONLY use dynamic API data, NO hardcoded fallbacks
    pharmacists: pharmacistNames,
    roomNames: dynamicRooms,
    blockOutTypes,
    serviceTypes: dynamicServices,
    defaultPharmacyServices,
    serviceTypesInfo,
    serviceColors: getServiceColors(darkMode),
    serviceColorMap, // API service colors (hex)
    serviceNameMap, // Service ID to name mapping
    timeSlots: FULL_DAY_TIME_SLOTS,
    dayNames,
    members,
    clinicians,
    resources,
    currentUserEmail,

    // filters state
    selectedPharmacists,
    selectedServiceTypes,
    selectedRooms,
    selectedBlockOutTypes,
    googleSyncEvents,
    pharmacyRooms: roomInventory,
    dynamicRooms,
    dynamicResources,
    filteredTasks,

    // filtered access
    getDaysInMonth,
    getWeekDays,
    getThreeDays,
    getWorkWeekDays,
    getFilteredBookingsForDate,
    getFilteredBookingsForTimeSlot,
    getFilteredBlockOutsForDate,
    getFilteredBlockOutsForTimeSlot,
    getFilteredTasksForDate,
    getFilteredTasksForTimeSlot,
    handleStartConsultation,

    // utils
    formatDate,
    formatDateFromDate,
    getViewTitle,

    // actions
    navigate,
    onCalendarSelect,
    handleViewDay,

    handleServiceTypeChange,
    handlePharmacistChange,
    handleSelectAllServices,
    handleSelectAllPharmacists,
    handleRoomChange,
    handleSelectAllRooms,
    handleBlockOutTypeChange,
    handleSelectAllBlockOutTypes,

    onCreateBooking,
    onCreateBookingForDate,
    onCreateBookingWithDefaults,
    onShowEditBooking,
    onEditBooking,
    onDeleteBooking,

    onCreateBlockOut,
    onShowEditBlockOut,
    onEditBlockOut,
    onDeleteBlockOut,

    openNewAppointment,
    closeModal,
    onSaveFromModal,
    closeBlockOutModal,
    onSaveFromBlockOutModal,
    setView,
    allUserWeekAvailability,
    inviteMemberLogs,
  };
}
